/**
 * Save and load.
 *
 * The field list here is deliberately the same one as `hashWorld` — that
 * function is the project's definition of "what counts as state", and a save
 * that carries anything less will load into a world that immediately diverges
 * from the one that was saved. The round-trip test asserts exactly that: save,
 * load, run both forward, and the hashes must still match.
 *
 * Derived caches (spatial hash, flow fields, path cache, the per-tick event
 * list) are not saved. They are pure functions of what is, and rebuilding them
 * is cheaper than storing them.
 */

import { MAP_TILES } from './constants.ts';
import {
  emptyResources,
  RESOURCE_KEYS,
  TaxPolicy,
  Team,
  type FieldHill,
  type FieldObstacle,
  type ResourceBundle,
} from './types.ts';
import {
  createWorld,
  defaultMods,
  emptyEquipped,
  emptyHero,
  emptyProfile,
  invalidateCompanyIndex,
  statsOf,
  type Company,
  type HeroState,
  type ItemStack,
  type Mods,
  type SpawnOrder,
  type PendingEvent,
  type World,
  type WorldConfig,
  type Zone,
} from './world.ts';
import { generateMap } from './map.ts';
import { seedResourceNodes } from './systems/nodes.ts';
import { recount } from './systems/towers.ts';
import { deriveSeed, makeRng } from '../core/rng.ts';
import { race, raceOfFaction } from '../data/races.ts';
import { OLD_SAVE_CHILD_SHARE, OLD_SAVE_ELDER_SHARE } from '../data/demography.ts';
import { loadCampaign, saveCampaign, type CampaignSave } from './campaign/save.ts';
import {
  SCENARIOS,
  type BattleArmy,
  type BattleLoadout,
  type BattleState,
  type ScenarioKind,
} from './battle.ts';
import { rebuildObstacleGrid } from './obstacles.ts';
import { rebuildHeightGrid } from './field.ts';
import { isRetiredPower, RETIRED_POWERS } from '../data/powers.ts';

/**
 * 6 → 7: whose walls these are, where a company has been told to walk, and
 * whether a host is still forming up after a fight on the road.
 *
 * Three fields from two branches of the same batch, which is why the number is
 * 7 once rather than 7 and 8.
 *
 * Both migrations are `??` defaults applied unconditionally rather than inside
 * a `version <` block, which is the practice this file settled on for a reason
 * worth restating: the hazard is not the old save, it is a save *stamped with
 * the current number* written by a build from before a field existed, which is
 * what every parallel worktree produces. An idempotent default is correct
 * against all of them and costs nothing against a save that already carries the
 * field.
 *
 * By the batch agreement in `COORDINATION.md` this number goes to 7 at most
 * once. If another branch also reaches 7 the merge keeps one 7 and both
 * migrations; nobody writes 8.
 *
 * **And it did.** The balance pass reached 7 independently for the diet —
 * `dietVariety` on the World, and a strip of the five retired Sovereign Powers
 * out of `unlockedPowers` and `powerTiers`. This merge is the case the
 * paragraph above describes: one 7, and every migration kept. Nobody writes 8.
 */
/**
 * 7 → 8: a crown may hold more than one town.
 *
 * Three things, and all three are `??` defaults applied unconditionally rather
 * than inside a `version < 8` block, for the reason restated above and worth
 * restating again: the hazard is not the old save, it is a save *stamped with
 * the current number* by a build that has never heard of the field, which is
 * what every parallel worktree in this batch is writing right now.
 *
 *   • `ProvinceState.settled` — which of your holdings are places you have
 *     stood in, and therefore which of them have a settlement world of their
 *     own. See `campaign/save.ts`, where the capital is additionally forced to
 *     `settled` on load: every save ever written has a capital world in the
 *     envelope beside it, and a capital that loaded as unsettled would offer to
 *     found a town on the ground the player is standing on.
 *   • `ProvinceState.wreck` — the sack owed to whoever next walks in.
 *   • `WorldConfig.province` — which holding a settlement world *is*. It rides
 *     in the config, which is saved verbatim, so the migration is `?? undefined`
 *     and the fallback is `c.capital`: every settlement world written before
 *     this field existed was the capital, so the default is not a guess.
 *
 * The **envelope** grew too, and that is not this number's business: a campaign
 * is now the realm plus *n* settlement worlds rather than the realm plus one,
 * and the envelope in `src/ui/storage.ts` carries its own version for exactly
 * that split. Each town in it is an ordinary `SaveGame` through this file.
 *
 * By the batch agreement in `COORDINATION.md` this number goes to 8 at most
 * once and nobody writes 9. If another branch also reaches 8, keep one 8 and
 * every migration.
 */
export const SAVE_VERSION = 8;
export const SAVE_KEY = 'hearth.save.v1';

/** Fields copied verbatim from a live unit slot. */
const UNIT_FIELDS = [
  'typeId', 'team', 'faction', 'x', 'y', 'vx', 'vy', 'facing', 'hp', 'maxHp',
  'morale', 'state', 'stateT', 'attackCd', 'swing', 'flags', 'companyId',
  'slotIdx', 'targetH', 'targetBuilding', 'targetWall', 'goalX', 'goalY',
  'radius', 'speed', 'job', 'workSite', 'slowT', 'poisonT', 'fearT', 'stunT',
  'rallyT', 'healT', 'madnessT', 'shieldT', 'lifeT', 'knockX', 'knockY',
  'lastHitTick', 'heroId', 'ammo', 'fatigue', 'wallTile',
  'carry', 'carryKind', 'nodeTarget', 'haulT',
] as const;

/**
 * The node store's columns.
 *
 * Four, and `tile` is one of them rather than an (x, y) pair — see the note on
 * `ResourceNodeStore`. At eleven thousand nodes this is 13 bytes a node before
 * base64, about 190 KiB of a save whose map layers are already the best part of
 * a megabyte, and it packs through exactly the same `saveStore`/`loadStore` the
 * other three stores use because the store has the same shape.
 */
const NODE_FIELDS = ['tile', 'kind', 'amount', 'max'] as const;

const BUILDING_FIELDS = [
  'typeId', 'tx', 'ty', 'hp', 'maxHp', 'team', 'build', 'workers', 'tier',
  'cooldown', 'stock', 'variant', 'lastHitTick', 'targetH',
] as const;

const PROJ_FIELDS = [
  'x', 'y', 'z', 'vx', 'vy', 'vz', 'ttl', 'damage', 'dmgType', 'team', 'kind',
  'splash', 'targetH', 'ownerH', 'tx', 'ty',
] as const;

export interface SaveGame {
  version: number;
  savedAt: number;
  config: WorldConfig;

  tick: number;
  rng: number;
  rngDirector: number;
  rngEvent: number;
  rngCampaign: number;
  /** The hoard's stream. Absent in every save written before items existed. */
  rngSpoils: number;

  res: ResourceBundle;
  resCap: number;
  phase: number;
  phaseT: number;
  wave: number;
  age: number;
  hearthSaved: boolean;
  unitRanks: Record<string, number>;
  researchingAge: number;
  ageResearch: number;
  /** The Long Ledger. Days since the run began, and seconds into today. */
  calendarDay: number;
  calendarT: number;
  /** Held because the player is away fighting. See `World.battleHold`. */
  battleHold: boolean;
  /** Who these people are, and which house of them. See `World.raceId`. */
  raceId: string;
  leaderId: string;
  pop: number;
  popCap: number;
  /** The standing pool. See `World.mana`. */
  mana: number;
  manaMax: number;
  /** The reliquary and the pens. See `World.consecratedNames` and `World.thralls`. */
  consecratedNames: number;
  nameWork: number;
  thralls: number;
  /** The age bands. See `World.demography`; absent in saves from before them. */
  demography: { children: number; elders: number; grave: number };
  morale: number;
  taxPolicy: number;
  arrears: number;
  fedFraction: number;
  breadShare: number;
  dietVariety: number;
  rubbleCount: number;
  /** Buildings explicitly ordered repaired. See `World.repairOrders`. */
  repairOrders: number[];
  will: number;
  willMax: number;
  hearth: number;
  wallTeam: number;
  gameOver: boolean;
  causeOfDeath: string;
  victoryWave: number;
  nextCompanyId: number;

  /** Base64 of each mutable map layer. */
  map: Record<string, string>;
  mapMeta: { hearthX: number; hearthY: number; terrainVersion: number; gates: unknown[] };

  units: { slots: unknown; fields: Record<string, string | number[]> };
  buildings: { slots: unknown; fields: Record<string, string | number[]> };
  proj: { slots: unknown; fields: Record<string, string | number[]> };
  /**
   * The trees, the rocks and the ore. `null` in every save written before they
   * existed, which is not a hole: see the seeding branch in `loadWorld`, where
   * such a save has its wood re-derived from its own terrain.
   */
  nodes: { slots: unknown; fields: Record<string, string | number[]> } | null;

  companies: Company[];
  zones: Zone[];
  spawnQueue: SpawnOrder[];

  incomingGates: number[];
  incomingFaction: string;
  incomingComposition: Array<{ typeIdx: number; count: number }>;
  incomingModifiers: string[];
  incomingBudget: number;
  /** The guided run's lesson number, 0 on every ordinary run. */
  tutorialStep: number;
  profile: World['profile'];

  unlockedBuildings: string[];
  unlockedPowers: string[];
  powerCooldowns: Record<string, number>;
  powerTiers: Record<string, number>;
  relics: string[];
  techNodes: string[];
  relicOffer: string[];
  mods: Mods;
  /**
   * The bag with nothing equipped.
   *
   * Written out alongside `mods` rather than being recomputed on load, because
   * recomputing it is impossible: `mods` is `modsBase` folded with the kit, and
   * unfolding is the division this design refuses to do. Both bags are stored,
   * both are loaded verbatim, and the round-trip is therefore exact rather than
   * approximately right.
   */
  modsBase: Mods;
  /** The character sheet: class, level, skills, cooldowns and where he is. */
  hero: HeroState;
  /** The cart, and what is being worn out of it. */
  inventory: ItemStack[];
  equipped: number[];
  nextItemUid: number;

  /**
   * The Chronicle. A save taken with the modal open has to load with the modal
   * open — the modal is not a UI state, it is a frozen simulation waiting on an
   * answer, and dropping it on load would resume a world the player never
   * decided anything in.
   */
  pendingEvent: PendingEvent | null;
  eventHistory: string[];
  lastEventWave: number;

  /**
   * The strategic layer, or null for a settlement run. A campaign is the
   * longest-lived save this game produces, which is exactly why it goes through
   * the same versioned door as everything else rather than into a key of its own.
   */
  campaign: CampaignSave | null;

  /**
   * The battle, or null for a settlement run.
   *
   * `w.battle` is what switches the tick away from the settlement and the
   * director, so a save without it reloaded a battlefield that immediately
   * started running an economy on it: villagers who were not there, a build
   * timer counting down on an open field, and no horn to sound because nothing
   * remembered a battle was waiting to start. The scenario is stored by id
   * rather than by value, because `ScenarioDef` is content and content is
   * allowed to change between builds; the id is the only part of it that is
   * state.
   */
  battle: BattleSave | null;

  stats: ReturnType<typeof statsOf>;
}

/** A battle, flattened. `scenario` is an id; everything else is verbatim. */
export interface BattleSave {
  scenario: ScenarioKind;
  /** Both sides human. Absent in saves written before shared fields. */
  pvp?: boolean;
  player: BattleArmy;
  enemy: BattleArmy;
  /** The second banner a side. Absent in saves written before 2v2. */
  playerAlly?: BattleArmy | null;
  enemyAlly?: BattleArmy | null;
  /** The army the player chose. Absent in saves written before the loadout. */
  loadout?: BattleLoadout;
  /**
   * Whether the player may re-pick it. Absent before the muster list, and it
   * reads back as **false** — the safe direction, and the right one: a save
   * written before this existed was written by a build where nobody could
   * re-muster anything.
   */
  freeMuster?: boolean;
  /** Trees and rocks. Absent in saves written before the scatter. */
  obstacles?: FieldObstacle[];
  /**
   * The relief. Absent in saves written before the field had any, and it reads
   * back as an empty list — which is the honest description of what those
   * fields were: flat. No `SAVE_VERSION` bump, for the same reason `obstacles`
   * needed none; an absent optional with a `??` default applied unconditionally
   * is a migration that cannot be got wrong by a save stamped with the current
   * number by a build that has never heard of the field.
   */
  hills?: FieldHill[];
  deploying: boolean;
  elapsed: number;
  outcome: number;
  deployZone: { x0: number; y0: number; x1: number; y1: number };
  points: number;
  log: string[];
}

function saveArmy(a: BattleArmy): BattleArmy {
  return { ...a, companies: a.companies.slice() };
}

/**
 * Read an army back, filling in the two fields that did not used to exist.
 *
 * `team` is the trap and it is the typed-array trap in a different coat: leave
 * it absent and it comes back `undefined`, which is not 0 here but is worse —
 * every alliance test on it would answer for `Alliance.None` and the army would
 * be at war with nobody. So it is defaulted **by position**, from the caller,
 * which is exactly right: before 2v2 there were only ever two armies, `player`
 * was `Team.Player` and `enemy` was `Team.Invader`, without exception.
 */
function loadArmy(a: BattleArmy, team: number): BattleArmy {
  return {
    ...a,
    companies: (a.companies ?? []).slice(),
    team: a.team ?? team,
    routed: a.routed ?? false,
  };
}

function saveBattle(b: BattleState): BattleSave {
  return {
    scenario: b.scenario.id,
    pvp: b.pvp,
    player: saveArmy(b.player),
    enemy: saveArmy(b.enemy),
    playerAlly: b.playerAlly ? saveArmy(b.playerAlly) : null,
    enemyAlly: b.enemyAlly ? saveArmy(b.enemyAlly) : null,
    loadout: { budget: b.loadout.budget, picks: b.loadout.picks.map((p) => ({ ...p })) },
    freeMuster: b.freeMuster,
    obstacles: b.obstacles.map((o) => ({ ...o })),
    hills: b.hills.map((h) => ({ ...h })),
    deploying: b.deploying,
    elapsed: b.elapsed,
    outcome: b.outcome,
    deployZone: { ...b.deployZone },
    points: b.points,
    log: b.log.slice(),
  };
}

function loadBattle(s: BattleSave | null | undefined): BattleState | undefined {
  if (!s) return undefined;
  return {
    // A save that names a scenario this build no longer has falls back to the
    // first rather than throwing: the armies on the field are the real state.
    scenario: SCENARIOS.find((sc) => sc.id === s.scenario) ?? SCENARIOS[0],
    // A save from before shared fields existed is a fight against the machine.
    pvp: s.pvp === true,
    player: loadArmy(s.player, Team.Player),
    enemy: loadArmy(s.enemy, Team.Invader),
    // A save from before 2v2 is a 1v1, which is the only thing it can have been.
    playerAlly: s.playerAlly ? loadArmy(s.playerAlly, Team.Ally) : null,
    enemyAlly: s.enemyAlly ? loadArmy(s.enemyAlly, Team.Warband) : null,
    // A save from before the loadout was handed its army rather than choosing
    // it; an empty purse with no picks in it is the honest description of that,
    // and it changes nothing on the field, which is already standing.
    freeMuster: s.freeMuster ?? false,
    loadout: {
      budget: s.loadout?.budget ?? s.points ?? 0,
      picks: (s.loadout?.picks ?? []).map((p) => ({ ...p })),
    },
    obstacles: (s.obstacles ?? []).map((o) => ({ ...o })),
    hills: (s.hills ?? []).map((h) => ({ ...h })),
    deploying: s.deploying,
    elapsed: s.elapsed,
    outcome: s.outcome,
    deployZone: { ...s.deployZone },
    points: s.points,
    log: (s.log ?? []).slice(),
  };
}

// ── Binary helpers ─────────────────────────────────────────────────────────

/**
 * Base64, written out by hand.
 *
 * This used `btoa` and `atob`, which are `window` methods — short enough names
 * to read as standard library, in a file whose own closing comment argues that
 * this layer has to stay headless. They happen to exist in Node too, which is
 * why nothing caught it; on a runtime where they do not, saving would simply
 * throw. Twenty lines of table lookup costs nothing and owes nobody a platform.
 */
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64_INV = (() => {
  const t = new Int16Array(128).fill(-1);
  for (let i = 0; i < B64.length; i++) t[B64.charCodeAt(i)] = i;
  return t;
})();

function toBase64(view: ArrayBufferView): string {
  const bytes = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  const parts: string[] = [];
  let chunk = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2];
    chunk += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + B64[n & 63];
    // Flushing periodically keeps the intermediate strings out of the rope-y
    // worst case that a single += over a megabyte of map data would produce.
    if (chunk.length >= 0x8000) {
      parts.push(chunk);
      chunk = '';
    }
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = bytes[i] << 16;
    chunk += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + '==';
  } else if (rest === 2) {
    const n = (bytes[i] << 16) | (bytes[i + 1] << 8);
    chunk += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + B64[(n >> 6) & 63] + '=';
  }
  parts.push(chunk);
  return parts.join('');
}

function fromBase64(b64: string, target: ArrayBufferView): void {
  const bytes = new Uint8Array(target.buffer, target.byteOffset, target.byteLength);
  let acc = 0;
  let bits = 0;
  let out = 0;
  for (let i = 0; i < b64.length && out < bytes.length; i++) {
    const c = b64.charCodeAt(i);
    const v = c < 128 ? B64_INV[c] : -1;
    if (v < 0) continue; // padding, newlines, anything that is not a digit
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[out++] = (acc >> bits) & 0xff;
    }
  }
}

/**
 * Decode into a fresh byte buffer, for the cases where the caller does not
 * already own an array of the right size — a packed store field arrives as
 * bytes whose width is named in the blob rather than known up front.
 */
function fromBase64String(b64: string): Uint8Array {
  let n = 0;
  for (let i = 0; i < b64.length; i++) {
    const c = b64.charCodeAt(i);
    if (c < 128 && B64_INV[c] >= 0) n++;
  }
  const bytes = new Uint8Array((n * 3) >> 2);
  let acc = 0;
  let bits = 0;
  let out = 0;
  for (let i = 0; i < b64.length && out < bytes.length; i++) {
    const c = b64.charCodeAt(i);
    const v = c < 128 ? B64_INV[c] : -1;
    if (v < 0) continue;
    acc = (acc << 6) | v;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes[out++] = (acc >> bits) & 0xff;
    }
  }
  return bytes;
}

/**
 * Map layers, run-length encoded when that is smaller.
 *
 * A layer is one value per tile and the map is 384x384 — 147,456 tiles. Most
 * layers are overwhelmingly one value: walls stand on a few hundred tiles of a
 * hundred and fifty thousand, rubble on fewer, and `wallHp`/`wallMaxHp`/
 * `rubbleWork` are `Float32Array`s that are zero almost everywhere. Stored flat
 * those three alone were 2.25 MiB of base64 describing, mostly, nothing.
 *
 * So each layer is offered as runs of (count, value) and kept in that form only
 * if it actually came out smaller — terrain is genuinely varied and compresses
 * badly, and forcing it would make the file bigger. The tag says which, so the
 * loader never has to guess, and an untagged string is the old flat format,
 * which is what lets this land without a save-version bump.
 */
function packLayer(view: ArrayBufferView & ArrayLike<number>): string {
  const n = view.length;
  /**
   * The exact point past which run-length encoding cannot win.
   *
   * A run costs two float64s — sixteen bytes — against `byteLength` for the
   * flat form, so the moment the run count exceeds `byteLength / 16` the
   * answer is already known and the rest of the scan is wasted. That matters
   * because the layers which compress badly are exactly the ones that would
   * otherwise be scanned all the way to the end: `terrain` is varied over every
   * tile and bails here after a fraction of the work.
   */
  const giveUp = view.byteLength >> 4;
  const runs: number[] = [];
  let i = 0;
  while (i < n) {
    const v = view[i];
    let j = i + 1;
    while (j < n && view[j] === v) j++;
    runs.push(j - i, v);
    i = j;
    if (runs.length > giveUp) return `raw:${toBase64(view)}`;
  }
  // Compare the *byte* costs rather than encoding both and measuring the
  // strings: base64 is a fixed 4/3 of its input, so the comparison is exact and
  // the loser is never encoded. Encoding both was costing more than the whole
  // rest of the save put together.
  const rleBytes = runs.length * Float64Array.BYTES_PER_ELEMENT;
  if (rleBytes >= view.byteLength) return `raw:${toBase64(view)}`;
  return `rle:${toBase64(new Float64Array(runs))}`;
}

function unpackLayer(v: string | undefined, view: ArrayBufferView & { [k: number]: number; length: number }): void {
  if (!v) return;
  if (v.startsWith('rle:')) {
    const bytes = fromBase64String(v.slice(4));
    const buf = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(buf).set(bytes);
    const runs = new Float64Array(buf);
    let at = 0;
    for (let r = 0; r + 1 < runs.length; r += 2) {
      const count = runs[r];
      const val = runs[r + 1];
      for (let k = 0; k < count && at < view.length; k++) view[at++] = val;
    }
    return;
  }
  // `raw:` prefix, or an untagged string from before this existed.
  fromBase64(v.startsWith('raw:') ? v.slice(4) : v, view);
}

// ── Save ───────────────────────────────────────────────────────────────────

/** Pull the live slots of a store out into plain number arrays. */
/**
 * Pack the live slots of a store, one base64 blob per field.
 *
 * These used to be JSON number arrays, and the map layers beside them were
 * already base64 — which was survivable at six thousand units and is not at
 * twenty thousand. A float written as JSON costs about ten characters
 * ("-12.3456789,"); the same float as four bytes of base64 costs 5.33. At
 * eighteen thousand units across forty-two fields that difference was 4.9 MiB
 * of a 8.8 MiB save, against a `localStorage` quota that is usually five.
 *
 * Each field keeps the width of the array it came from: an `Int32Array` field
 * packs as int32 and a `Float32Array` field as float32, so nothing is widened
 * and nothing is rounded. A field whose live array is a plain JS array — none
 * today, but the stores are not sealed — falls back to the old number list, and
 * the loader accepts either form. That fallback is what lets this land without
 * a save-version bump: an old save is simply one where every field took the
 * slow path.
 */
function packField(src: ArrayLike<number>, alive: Uint8Array, high: number, count: number): string | number[] {
  const ctor = (src as { constructor?: unknown }).constructor as
    | (new (n: number) => ArrayLike<number> & ArrayBufferView)
    | undefined;
  const typed =
    ctor === Float32Array || ctor === Float64Array || ctor === Int32Array ||
    ctor === Int16Array || ctor === Uint16Array || ctor === Uint8Array || ctor === Int8Array ||
    ctor === Uint32Array;
  if (!typed || !ctor) {
    const out: number[] = [];
    for (let i = 0; i < high; i++) if (alive[i] !== 0) out.push(src[i]);
    return out;
  }
  const packed = new ctor(count) as ArrayLike<number> & ArrayBufferView & { [k: number]: number };
  let c = 0;
  for (let i = 0; i < high; i++) if (alive[i] !== 0) packed[c++] = src[i];
  return `${ctor.name}:${toBase64(packed)}`;
}

function saveStore(
  store: { slots: { high: number; alive: Uint8Array; save(): unknown } },
  fields: readonly string[],
): { slots: unknown; fields: Record<string, string | number[]> } {
  const out: Record<string, string | number[]> = {};
  const high = store.slots.high;
  const alive = store.slots.alive;
  let count = 0;
  for (let i = 0; i < high; i++) if (alive[i] !== 0) count++;
  for (const f of fields) {
    out[f] = packField((store as unknown as Record<string, ArrayLike<number>>)[f], alive, high, count);
  }
  return { slots: store.slots.save(), fields: out };
}

/** How many live slots a packed or unpacked column describes. */
function fieldLength(v: string | number[] | undefined): number {
  if (v === undefined) return 0;
  if (typeof v !== 'string') return v.length;
  return unpackField(v).length;
}

/** The other half of `packField`. Accepts a base64 blob or an old number list. */
const TYPED: Record<string, new (b: ArrayBuffer) => ArrayLike<number>> = {
  Float32Array,
  Float64Array,
  Int32Array,
  Uint32Array,
  Int16Array,
  Uint16Array,
  Int8Array,
  Uint8Array,
};

function unpackField(v: string | number[] | undefined): ArrayLike<number> {
  if (v === undefined) return [];
  if (typeof v !== 'string') return v;
  const cut = v.indexOf(':');
  const ctor = TYPED[v.slice(0, cut)];
  if (!ctor) return [];
  const bytes = fromBase64String(v.slice(cut + 1));
  const buf = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buf).set(bytes);
  return new ctor(buf);
}

function loadStore(
  store: { slots: { high: number; alive: Uint8Array; load(s: never): void }; reset(): void },
  data: { slots: unknown; fields: Record<string, string | number[]> },
  fields: readonly string[],
): void {
  store.reset();
  store.slots.load(data.slots as never);
  const cols: Record<string, ArrayLike<number>> = {};
  for (const f of fields) cols[f] = unpackField(data.fields[f]);
  let cursor = 0;
  for (let i = 0; i < store.slots.high; i++) {
    if (store.slots.alive[i] === 0) continue;
    for (const f of fields) {
      const arr = (store as unknown as Record<string, { [k: number]: number }>)[f];
      arr[i] = cols[f][cursor];
    }
    cursor++;
  }
}

/**
 * Serialise a world.
 *
 * `savedAt` is a parameter rather than a `Date.now()` taken here, for two
 * reasons: the wall clock is a platform detail and this layer is headless, and
 * a function that stamps the time is not pure — two saves of the same world
 * would differ, which is precisely the property the round-trip test exists to
 * check. The storage layer knows what time it is; see `src/ui/storage.ts`.
 */
export function saveWorld(w: World, savedAt = 0): SaveGame {
  const map: Record<string, string> = {
    terrain: packLayer(w.map.terrain),
    wall: packLayer(w.map.wall),
    wallHp: packLayer(w.map.wallHp),
    wallMaxHp: packLayer(w.map.wallMaxHp),
    wallTier: packLayer(w.map.wallTier),
    building: packLayer(w.map.building),
    decor: packLayer(w.map.decor),
    stain: packLayer(w.map.stain),
    rubble: packLayer(w.map.rubble),
    rubbleWork: packLayer(w.map.rubbleWork),
  };

  return {
    version: SAVE_VERSION,
    savedAt,
    config: { ...w.config },

    tick: w.tick,
    rng: w.rng.s,
    rngDirector: w.rngDirector.s,
    rngEvent: w.rngEvent.s,
    rngCampaign: w.rngCampaign.s,
    rngSpoils: w.rngSpoils.s,

    res: { ...w.res },
    resCap: w.resCap,
    phase: w.phase,
    phaseT: w.phaseT,
    wave: w.wave,
    age: w.age,
    hearthSaved: w.hearthSaved,
    unitRanks: { ...w.unitRanks },
    researchingAge: w.researchingAge,
    ageResearch: w.ageResearch,
    calendarDay: w.calendarDay,
    calendarT: w.calendarT,
    battleHold: w.battleHold,
    raceId: w.raceId,
    leaderId: w.leaderId,
    pop: w.pop,
    popCap: w.popCap,
    mana: w.mana,
    manaMax: w.manaMax,
    consecratedNames: w.consecratedNames,
    nameWork: w.nameWork,
    thralls: w.thralls,
    demography: { ...w.demography },
    morale: w.morale,
    taxPolicy: w.taxPolicy,
    arrears: w.arrears,
    fedFraction: w.fedFraction,
    breadShare: w.breadShare,
    dietVariety: w.dietVariety,
    rubbleCount: w.rubbleCount,
    repairOrders: w.repairOrders.slice(),
    will: w.will,
    willMax: w.willMax,
    hearth: w.hearth,
    wallTeam: w.wallTeam,
    gameOver: w.gameOver,
    causeOfDeath: w.causeOfDeath,
    victoryWave: w.victoryWave,
    nextCompanyId: w.nextCompanyId,

    map,
    mapMeta: {
      hearthX: w.map.hearthX,
      hearthY: w.map.hearthY,
      terrainVersion: w.map.terrainVersion,
      gates: w.map.gates.map((g) => ({ ...g })),
    },

    units: saveStore(w.units as never, UNIT_FIELDS),
    buildings: saveStore(w.buildings as never, BUILDING_FIELDS),
    proj: saveStore(w.proj as never, PROJ_FIELDS),
    nodes: saveStore(w.nodes as never, NODE_FIELDS),

    companies: w.companies.map((c) => ({ ...c, members: c.members.slice(), route: c.route.slice() })),
    zones: w.zones.map((z) => ({ ...z })),
    spawnQueue: w.spawnQueue.map((s) => ({ ...s })),

    incomingGates: w.incomingGates.slice(),
    incomingFaction: w.incomingFaction,
    incomingComposition: w.incomingComposition.map((c) => ({ ...c })),
    incomingModifiers: w.incomingModifiers.slice(),
    incomingBudget: w.incomingBudget,
    tutorialStep: w.tutorialStep,
    profile: { ...w.profile },

    unlockedBuildings: w.unlockedBuildings.slice(),
    unlockedPowers: w.unlockedPowers.slice(),
    powerCooldowns: { ...w.powerCooldowns },
    powerTiers: { ...w.powerTiers },
    relics: w.relics.slice(),
    techNodes: w.techNodes.slice(),
    relicOffer: w.relicOffer.slice(),
    mods: { ...w.mods },
    modsBase: { ...w.modsBase },
    hero: { ...w.hero, skills: w.hero.skills.slice(), cooldowns: { ...w.hero.cooldowns } },
    inventory: w.inventory.map((s) => ({ ...s })),
    equipped: w.equipped.slice(),
    nextItemUid: w.nextItemUid,

    pendingEvent: w.pendingEvent ? { ...w.pendingEvent, choices: w.pendingEvent.choices.slice() } : null,
    eventHistory: w.eventHistory.slice(),
    lastEventWave: w.lastEventWave,

    campaign: w.campaign ? saveCampaign(w.campaign) : null,
    battle: w.battle ? saveBattle(w.battle) : null,

    stats: JSON.parse(JSON.stringify(statsOf(w))),
  };
}

// ── Load ───────────────────────────────────────────────────────────────────

/**
 * Migrate an older save forward. Each step is deliberately small and additive;
 * anything that cannot be recovered gets the value a fresh run would have.
 */
function migrate(s: SaveGame): SaveGame {
  if (s.version < 2) {
    // v1 predated the production chain: give the new resources a floor rather
    // than leaving them undefined.
    const res = s.res as unknown as Record<string, number>;
    res.planks = res.planks ?? 0;
    res.bread = res.bread ?? 0;
  }
  if (s.version < 3) {
    // v2 predated ammo and fatigue on the unit store.
    // A v2 save predates packed fields by four versions, so `hp` is still a
    // plain number list here — but read its length rather than assuming, so
    // this stays correct if an old save is ever re-written by a new one.
    const rows = Array.isArray(s.units.fields.hp) ? s.units.fields.hp.length : 0;
    s.units.fields.ammo = s.units.fields.ammo ?? new Array<number>(rows).fill(0);
    s.units.fields.fatigue = s.units.fields.fatigue ?? new Array<number>(rows).fill(0);
    s.rubbleCount = s.rubbleCount ?? 0;
  }
  if (s.version < 4) {
    // v3 predated coin. An old settlement wakes up with a modest float and its
    // taxes set to Fair, which is the band that changes nothing — a save made
    // before money existed should not load into a debt crisis.
    const res = s.res as unknown as Record<string, number>;
    res.gold = res.gold ?? 150;
    s.taxPolicy = s.taxPolicy ?? TaxPolicy.Fair;
    s.arrears = s.arrears ?? 0;
  }
  /**
   * Version 5 was claimed three times.
   *
   * The Chronicle, the hero system and the world map were built in parallel, in
   * separate worktrees off the same commit, and each bumped 4 → 5 with a
   * migration for its own third. None was wrong; none could see the others. So
   * a save stamped 5 might carry Chronicle fields, hero fields, campaign
   * fields, or any combination, depending on which tree wrote it.
   *
   * Rather than guess, version 6 fills in whatever is absent. Every line here
   * is a `??` default and therefore idempotent, so running it against a save
   * that already has the field costs nothing and changes nothing. That is what
   * makes it safe to apply to v4 and every flavour of v5 alike.
   */
  if (s.version < 6) {
    // Predates the Chronicle: wake with nothing pending, an empty history and
    // an event stream freshly derived from the run seed — exactly the state a
    // run that had never drawn an event is in.
    s.rngEvent = s.rngEvent ?? deriveSeed(s.config.seed, 4441);
    s.pendingEvent = s.pendingEvent ?? null;
    s.eventHistory = s.eventHistory ?? [];
    s.lastEventWave = s.lastEventWave ?? -99;
    // Predates heroes: a run that never chose one, which is a legal state
    // rather than a broken one.
    s.hero = s.hero ?? emptyHero();
    // Predates the world map. The campaign RNG stream has to be derived exactly
    // the way `createWorld` derives it, or a settlement save from before the
    // strategic layer existed would load into a different hash than it saved at.
    s.rngCampaign = s.rngCampaign ?? deriveSeed(s.config.seed, 613);
    s.campaign = s.campaign ?? null;
    // Predates bought Ages: a run that was handed its Age by the old wave clock
    // simply is not researching anything, which is a legal standing state.
    s.researchingAge = s.researchingAge ?? 0;
    s.hearthSaved = s.hearthSaved ?? false;
    s.unitRanks = s.unitRanks ?? {};
    s.ageResearch = s.ageResearch ?? 0;
    // Predates the Long Ledger: a save taken before there was a calendar wakes
    // on the first of Mudmonth, Year 1, which is where a fresh run stands. The
    // `??` *is* the migration, and it is idempotent, so it is safe against a
    // save that already carries a date.
    s.calendarDay = s.calendarDay ?? 0;
    s.calendarT = s.calendarT ?? 0;
    /**
     * Predates the real-time realm. A save can only have been taken outside a
     * battle — `doSave` refuses in one, and always has — so "not held" is the
     * only state such a save can have been in, and `SAVE_VERSION` stays where it
     * is. The `??` is the migration, and it is idempotent.
     */
    s.battleHold = s.battleHold ?? false;
    // Predates the battle key. Every save that could possibly reach this line
    // was a settlement or a campaign, because a battle was not saveable at all
    // — the field did not exist, in either direction — so "no battle" is not a
    // guess here, it is the only thing such a save can have been.
    s.battle = s.battle ?? null;
  }
  /**
   * The Kindling's four goods, defaulted rather than versioned.
   *
   * Deliberately *outside* the `version < 6` block and applied to every save,
   * because the hazard is not an old save — it is a save stamped 6 that was
   * written by a build from before these keys existed, which is exactly what
   * every parallel worktree is producing right now. `SAVE_VERSION` is frozen by
   * agreement (see `COORDINATION.md`), so the `??` *is* the migration: it is
   * idempotent, it costs nothing against a save that already carries them, and
   * it is correct against every version that has ever been written.
   *
   * A settlement that has never seen a leyline wakes with none of them, which
   * is precisely where a fresh run stands.
   */
  const res = s.res as unknown as Record<string, number>;
  res.essence = res.essence ?? 0;
  res.magewood = res.magewood ?? 0;
  res.runestone = res.runestone ?? 0;
  res.ingots = res.ingots ?? 0;
  // The Bell, the Gilded and the Hollow, under the identical argument. Eight
  // more `??`s and not one version bump between them; a settlement that never
  // reached the third Age wakes holding none of these, which is where it stood.
  res.soulwood = res.soulwood ?? 0;
  res.voidstone = res.voidstone ?? 0;
  res.alloy = res.alloy ?? 0;
  res.quintessence = res.quintessence ?? 0;
  res.ashglass = res.ashglass ?? 0;
  res.heartiron = res.heartiron ?? 0;
  res.hollowglass = res.hollowglass ?? 0;
  res.gravesilver = res.gravesilver ?? 0;
  // The three race currencies, under the identical argument and the identical
  // agreement. These are the first keys that are gated by *who you are* rather
  // than by how far you have climbed, so most saves will carry all three at
  // zero for the whole of a run — which is exactly what a `??` default writes.
  res.ichor = res.ichor ?? 0;
  res.grudges = res.grudges ?? 0;
  res.scrap = res.scrap ?? 0;

  /**
   * The cart, defaulted rather than versioned, for the same reason and under
   * the same agreement: `SAVE_VERSION` is frozen at 6 while several branches are
   * in flight, so the `??` *is* the migration. Every line is idempotent.
   *
   * `modsBase` falls back to `mods`, and that is exactly right rather than a
   * shrug: a save written before items existed had nothing equipped, and a bag
   * with nothing equipped *is* the base. So the fallback reconstructs the truth
   * instead of guessing at it, and a run loaded from such a save can equip its
   * first item without losing a single relic.
   */
  /**
   * The curtain, defaulted rather than versioned, under the same agreement:
   * `SAVE_VERSION` is frozen while several branches are in flight, so the `??`
   * *is* the migration and every line of it is idempotent.
   *
   * `wallTile` cannot be defaulted by omission and that is the whole reason it
   * needs a line here. `loadStore` copies straight into an `Int32Array`, and an
   * absent column would write `undefined` — which lands as **0**, a perfectly
   * valid tile index in the top-left corner of the map. Every soldier in a save
   * written before this existed would wake up posted to tile zero and start
   * walking there. −1 per live slot is the only correct fill.
   */
  if (!s.units.fields.wallTile) {
    // Row count without caring how `hp` is stored: since fields became packed
    // base64 blobs a column is a string, not an array, so counting rows means
    // asking the decoder rather than calling `.map` on it.
    const rows = fieldLength(s.units.fields.hp);
    s.units.fields.wallTile = new Array<number>(rows).fill(-1);
  }
  for (const c of s.companies ?? []) {
    c.garrisonTile = c.garrisonTile ?? -1;
    /**
     * The way round, defaulted rather than versioned — see `Company.route`.
     *
     * An empty route is not a lossy guess: it means "march straight at the
     * order", which is exactly what every company in every save written before
     * this field existed was doing, and the first refresh re-lays a real one if
     * there is stone in the way.
     */
    c.route = c.route ?? [];
  }

  /**
   * The trees, the rocks and the ore, defaulted rather than versioned, under the
   * standing agreement that `SAVE_VERSION` is frozen at 6 while several branches
   * are in flight. Every line below is idempotent.
   *
   * **All four unit columns need an explicit fill, and only one of them looks
   * like it does.** The walls pass recorded the trap for `wallTile`: `loadStore`
   * copies straight into typed arrays, so an absent column writes `undefined`,
   * which lands as **0** in an `Int32Array` — and node slot zero is a real tree
   * on a real map that every worker in an old save would set out toward at once.
   * What that note did not have to say, because it was covering an integer
   * column, is that `undefined` written into a **`Float32Array` lands as `NaN`**,
   * not as zero. `carry` and `haulT` are float columns, so leaving them out
   * would give every villager in an old save a basket holding `NaN` — which
   * propagates into `w.res` at the first deposit and poisons the whole economy
   * with a number that is not even unequal to itself. Fill all four.
   *
   * `nodes` is left `null` here rather than being invented, because a node set
   * cannot be built without the map — see the branch in `loadWorld`, which
   * re-seeds an old save's wood from its own terrain. That works because
   * `seedResourceNodes` consumes no randomness: the trees a save gets on waking
   * are exactly the trees it would have had if this build had generated it.
   */
  const unitRows = fieldLength(s.units.fields.hp);
  if (!s.units.fields.carry) s.units.fields.carry = new Array<number>(unitRows).fill(0);
  if (!s.units.fields.carryKind) s.units.fields.carryKind = new Array<number>(unitRows).fill(0);
  if (!s.units.fields.nodeTarget) s.units.fields.nodeTarget = new Array<number>(unitRows).fill(-1);
  if (!s.units.fields.haulT) s.units.fields.haulT = new Array<number>(unitRows).fill(0);
  s.nodes = s.nodes ?? null;
  s.repairOrders = s.repairOrders ?? [];

  s.rngSpoils = s.rngSpoils ?? deriveSeed(s.config.seed, 7717);
  s.modsBase = s.modsBase ?? ({ ...s.mods } as Mods);
  s.inventory = s.inventory ?? [];
  s.equipped = s.equipped ?? [];
  s.nextItemUid = s.nextItemUid ?? 1;

  /**
   * The diet. A v6 world was fed on grain and bread alone, so it had exactly
   * one kind on the table and a variety of zero — which is what the `??`
   * writes, and what `stepUpkeep` would compute for it on its first tick
   * anyway. Idempotent: a v7 save already carries the field.
   */
  s.dietVariety = s.dietVariety ?? 0;
  /**
   * The guided run, defaulted rather than versioned, under the standing
   * agreement that `SAVE_VERSION` stays at 7 while six branches are in flight.
   *
   * Outside any `version <` block on purpose, and this is the case the rule was
   * written for: the hazard is not an old save, it is a save stamped **7** by a
   * build that has never heard of `tutorialStep`, which is what every other
   * worktree on this machine is writing tonight. `0` is not a guess — a run
   * saved by a build with no lessons in it had no lessons in it — and the `??`
   * is idempotent against a save that already carries the field.
   */
  s.tutorialStep = s.tutorialStep ?? 0;
  /**
   * The retired Powers.
   *
   * A v6 save can hold `starfall` in `unlockedPowers` and a bought tier for
   * it in `powerTiers`, and both would survive a load into a build where the
   * id resolves to nothing — an unlocked power the button list can draw, the
   * hotkey ring can select and `castPower` then silently refuses. Strip them
   * on the way in instead. Idempotent by construction: a save written after
   * the cut has none of them to strip.
   */
  s.unlockedPowers = s.unlockedPowers.filter((p) => !isRetiredPower(p));
  for (const p of RETIRED_POWERS) {
    delete s.powerTiers[p];
    delete s.powerCooldowns[p];
  }

  s.version = SAVE_VERSION;
  return s;
}

export function loadWorld(raw: SaveGame): World {
  const s = migrate({ ...raw });
  const w = createWorld(s.config);

  // The map is restored wholesale, but generate first so gates and the Hearth
  // position exist even if an old save is missing part of the metadata.
  generateMap(w.map, makeRng(deriveSeed(s.config.seed, 31)));

  const layers: Array<[string, ArrayBufferView & ArrayLike<number>]> = [
    ['terrain', w.map.terrain],
    ['wall', w.map.wall],
    ['wallHp', w.map.wallHp],
    ['wallMaxHp', w.map.wallMaxHp],
    ['wallTier', w.map.wallTier],
    ['building', w.map.building],
    ['decor', w.map.decor],
    ['stain', w.map.stain],
    ['rubble', w.map.rubble],
    ['rubbleWork', w.map.rubbleWork],
  ];
  for (const [k, view] of layers) {
    unpackLayer(s.map[k], view as ArrayBufferView & { [k: number]: number; length: number });
  }
  w.map.hearthX = s.mapMeta.hearthX;
  w.map.hearthY = s.mapMeta.hearthY;
  w.map.terrainVersion = s.mapMeta.terrainVersion;
  if (Array.isArray(s.mapMeta.gates) && s.mapMeta.gates.length > 0) {
    w.map.gates = s.mapMeta.gates as World['map']['gates'];
  }

  w.tick = s.tick;
  w.rng = makeRng(s.rng);
  w.rngDirector = makeRng(s.rngDirector);
  w.rngEvent = makeRng(s.rngEvent);
  w.rngCampaign = makeRng(s.rngCampaign);
  w.rngSpoils = makeRng(s.rngSpoils ?? deriveSeed(s.config.seed, 7717));

  Object.assign(w.res, emptyResources());
  for (const k of RESOURCE_KEYS) w.res[k] = s.res[k] ?? 0;
  w.resCap = s.resCap;
  w.phase = s.phase as World['phase'];
  /**
   * A battle in deployment and a capital on the strategic map both park
   * `phaseT` at `Infinity` — "there is no clock here". `JSON.stringify` turns
   * that into `null`, and a world whose phase timer is `null` is a world one
   * subtraction away from `NaN` in every downstream comparison. The `??` reads
   * the null back as the infinity it was written from.
   */
  w.phaseT = s.phaseT ?? Number.POSITIVE_INFINITY;
  w.wave = s.wave;
  w.age = s.age;
  w.hearthSaved = s.hearthSaved ?? false;
  w.unitRanks = { ...(s.unitRanks ?? {}) };
  w.researchingAge = s.researchingAge;
  w.ageResearch = s.ageResearch;
  w.calendarDay = s.calendarDay ?? 0;
  w.calendarT = s.calendarT ?? 0;
  w.battleHold = s.battleHold ?? false;
  /**
   * Identity, defaulted rather than versioned, under the standing agreement that
   * `SAVE_VERSION` is frozen while several branches are in flight.
   *
   * `raceId` falls back to whichever race plays as the save's faction, which is
   * exactly right rather than a shrug: a settlement written before races existed
   * *was* that race — it had that faction's bonuses, roster and architecture and
   * nothing else was on offer — so the fallback reconstructs the truth instead of
   * guessing at it. A pre-race save of the Pale Litany loads as the Pale Litany
   * and finds its Ossuaries buildable.
   *
   * `leaderId` falls back to '', which `foldIdentity` reads as the race's default
   * house. Every line here is idempotent.
   */
  w.raceId = s.raceId ?? raceOfFaction(w.config.factionId);
  w.leaderId = s.leaderId ?? '';
  w.pop = s.pop;
  w.popCap = s.popCap;
  w.mana = s.mana ?? 0;
  w.manaMax = s.manaMax ?? 0;
  /**
   * The reliquary and the pens, defaulted rather than versioned, under the
   * agreement in `COORDINATION.md`: `SAVE_VERSION` is frozen while several
   * branches are in flight, so the `??` *is* the migration and every line of it
   * is idempotent.
   *
   * `consecratedNames` falls back to **1**, not 0, and the difference is not
   * cosmetic. A Vigil save written before names existed had a working muster the
   * moment it was taken; waking it with an empty shelf would take that away
   * retroactively and leave the player unable to raise anything for a minute and
   * a half for reasons nothing in the game had told them. One name is the state
   * such a save was actually in. Every other people carries the field and never
   * reads it, so the default costs them nothing.
   */
  w.consecratedNames = s.consecratedNames ?? 1;
  w.nameWork = s.nameWork ?? 0;
  w.thralls = s.thralls ?? 0;
  /**
   * The age bands, defaulted rather than versioned, under the same standing
   * agreement: `SAVE_VERSION` is frozen while branches are in flight, so the
   * `??` *is* the migration and every line of it is idempotent.
   *
   * A save from before the bands carries none, and it wakes as **a fifth
   * children and a tenth elders** — the argument is at the constants in
   * `data/demography.ts`, but the short form: "all adults" would hand a stale
   * save a permanent economy edge over a played run, and 70% grown is *above*
   * the pyramid the rates settle to, so a woken town keeps its jobs staffed
   * and drifts gently to the same equilibrium everyone else lives at.
   * Fractions of `pop`, which every save carries; the first tick's reconcile
   * clamps to the villagers actually present. An ageless people (the Litany —
   * raised, not born) wakes at zero, which is the state `stepDemography` pins
   * it to anyway; the branch just keeps the first tick's hash honest.
   */
  const ageless = race(w.raceId).ageless === true;
  w.demography.children = s.demography?.children ?? (ageless ? 0 : Math.round(s.pop * OLD_SAVE_CHILD_SHARE));
  w.demography.elders = s.demography?.elders ?? (ageless ? 0 : Math.round(s.pop * OLD_SAVE_ELDER_SHARE));
  w.demography.grave = s.demography?.grave ?? 0;
  w.morale = s.morale;
  w.taxPolicy = s.taxPolicy;
  w.arrears = s.arrears;
  w.fedFraction = s.fedFraction;
  w.breadShare = s.breadShare;
  w.dietVariety = s.dietVariety;
  w.rubbleCount = s.rubbleCount;
  w.repairOrders = (s.repairOrders ?? []).slice();
  w.will = s.will;
  w.willMax = s.willMax;
  /**
   * Whose walls these are — see `World.wallTeam`.
   *
   * The `??` *is* the migration and it is idempotent. Every save written before
   * this field existed was a settlement or a campaign capital, and in both the
   * walls are the home side's, which is exactly what the default says. A
   * battle's own save is covered by the same default for the same reason: the
   * only fields that were saveable before this landed had no stonework anybody
   * else owned.
   */
  w.wallTeam = s.wallTeam ?? Team.Player;
  w.hearth = s.hearth;
  w.gameOver = s.gameOver;
  w.causeOfDeath = s.causeOfDeath;
  w.victoryWave = s.victoryWave;
  w.nextCompanyId = s.nextCompanyId;

  loadStore(w.units as never, s.units, UNIT_FIELDS);
  loadStore(w.buildings as never, s.buildings, BUILDING_FIELDS);
  loadStore(w.proj as never, s.proj, PROJ_FIELDS);
  /**
   * The wood, either as it was left or as it must have stood.
   *
   * A save that carries nodes is restored verbatim, felled stumps and all —
   * anything else would put a worked-out forest back and make the save a lie
   * about an hour of somebody's work. A save from before nodes existed carries
   * none, and is **re-seeded from its own restored terrain**: the walk is a pure
   * function of the map with no rolls in it, so such a save wakes with exactly
   * the trees it would have had if this build had generated it, standing exactly
   * where its forests are. `reindex` afterwards because `loadStore` writes
   * straight into the columns and knows nothing of the tile index.
   */
  if (s.nodes) {
    loadStore(w.nodes as never, s.nodes, NODE_FIELDS);
    w.nodes.reindex();
  } else {
    seedResourceNodes(w.map, w.nodes);
  }

  w.companies = s.companies.map((c) => ({ ...c, members: c.members.slice(), route: (c.route ?? []).slice() }));
  invalidateCompanyIndex(w);
  w.zones = s.zones.map((z) => ({ ...z }));
  w.spawnQueue = s.spawnQueue.map((o) => ({ ...o }));

  w.incomingGates = s.incomingGates.slice();
  w.incomingFaction = s.incomingFaction;
  w.incomingComposition = s.incomingComposition.map((c) => ({ ...c }));
  w.incomingModifiers = s.incomingModifiers.slice();
  w.incomingBudget = s.incomingBudget;
  w.tutorialStep = s.tutorialStep ?? 0;
  w.profile = { ...emptyProfile(), ...s.profile };

  w.unlockedBuildings = s.unlockedBuildings.slice();
  w.unlockedPowers = s.unlockedPowers.slice();
  w.powerCooldowns = { ...s.powerCooldowns };
  w.powerTiers = { ...s.powerTiers };
  w.relics = s.relics.slice();
  w.techNodes = s.techNodes.slice();
  w.relicOffer = s.relicOffer.slice();
  w.mods = { ...defaultMods(), ...s.mods };
  /**
   * Both bags, verbatim, and neither derived from the other.
   *
   * `mods` could in principle be rebuilt here from `modsBase` and the equipped
   * list — and doing so would be a slow, silent way to lose a modifier that some
   * older build folded in and this one no longer knows how to reproduce. Stored
   * state loads as stored state; `refoldEquipment` is what keeps them in step
   * from then on.
   */
  w.modsBase = { ...defaultMods(), ...(s.modsBase ?? s.mods) };
  w.inventory = (s.inventory ?? []).map((i) => ({ ...i }));
  // Padded rather than trusted: a save could carry fewer slots than this build
  // has, and a short array would make `equipped[4]` undefined for the rest of
  // the run.
  w.equipped = emptyEquipped();
  for (let i = 0; i < Math.min(w.equipped.length, (s.equipped ?? []).length); i++) {
    w.equipped[i] = s.equipped[i];
  }
  w.nextItemUid = s.nextItemUid ?? 1;
  w.hero = {
    ...emptyHero(),
    ...s.hero,
    skills: s.hero.skills.slice(),
    cooldowns: { ...s.hero.cooldowns },
  };

  w.pendingEvent = s.pendingEvent
    ? { ...s.pendingEvent, choices: s.pendingEvent.choices.slice() }
    : null;
  w.eventHistory = s.eventHistory.slice();
  w.lastEventWave = s.lastEventWave;
  w.campaign = loadCampaign(s.campaign);
  w.battle = loadBattle(s.battle);
  // The scatter's tile bitmap is derived from the list that has just been read,
  // exactly as the flow fields below are derived from the map. Rebuilt here
  // rather than lazily because the collision pass reads it on the first tick.
  rebuildObstacleGrid(w);
  // The relief's bitmap, from the same door and for the same reason: a derived
  // cache is not saved, it is rebuilt from the state that was.
  rebuildHeightGrid(w);

  Object.assign(statsOf(w), s.stats);

  // Derived state is never saved; force it all to rebuild.
  w.dirty.flow = true;
  w.dirty.districts = true;
  /**
   * **`dirty.jobs` is deliberately cleared here**, and it used to be set.
   *
   * It looks like the same "force the derived thing to rebuild" line as the two
   * above it, and it is not, because the job assignment is **not derived** —
   * `u.job`, `u.workSite` and `b.workers` are all saved columns. Nothing needs
   * rebuilding, so the flag bought nothing.
   *
   * What it cost was determinism, and the mechanism is worth writing down
   * because the same shape will be tempting again. `assignJobs` re-offers a
   * *slice* of the hands, not all of them: the turn comes round by unit index
   * plus `phase = ((w.tick / JOB_REASSIGN_TICKS) | 0) % REBALANCE_SLICES`.
   * Setting the flag ran that pass on a tick the saved world would never have
   * run it on, so a different slice of villagers was re-offered, some of them
   * moved to a different site, and from the very first tick the loaded game was
   * a different game from the one that was saved.
   *
   * Measured on four seeds played to a *completed* siege — save, load, step
   * both once: the hashes disagreed on all four. With this line gone they agree
   * on all four. It reproduces only after a siege ends, which is why
   * `tests/save.test.ts` never saw it: its fixture stops mid-siege.
   *
   * A villager whose workplace burned down is still re-placed immediately —
   * `assignJobs` handles that case regardless of whose turn it is — and the
   * whole pass still runs on its own schedule, so a save carrying a `workSite`
   * that no longer makes sense is corrected within `JOB_REASSIGN_TICKS` either
   * way. Nothing waits on the flag.
   *
   * It is cleared rather than merely left alone because `createWorld` opens
   * every world with `jobs: true` — correct for a town being founded, wrong for
   * one being woken with its hands already at their benches.
   */
  w.dirty.jobs = false;
  w.paths.clear();
  w.flowMain.stamp = -1;
  w.flowEcon.stamp = -1;
  w.flowHome.stamp = -1;
  w.events.length = 0;
  w.spatial.rebuild(w.units);
  // `counts` is derived, but it is read *before* it is recomputed within a tick:
  // the director checks `counts.enemy === 0` to decide a wave is over. Loading
  // without this left every count at zero, so the first tick after a load ended
  // the siege outright and the two worlds diverged immediately.
  recount(w);

  return w;
}

/**
 * Serialisation lives here; *storage* does not. Writing to localStorage is a
 * platform concern, and `src/sim` has to stay headless — the layering test
 * caught this the moment it was written in the wrong place, which is exactly
 * what it is for.
 *
 * See `src/ui/storage.ts` for the browser side.
 */
export const SAVE_MAP_TILES = MAP_TILES;
